import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui-kit";
import { fmtMoney } from "@/components/EventCard";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

type Row = { account_id: string; business_name: string; attended_bookings: number; finished_events: number; model: string; amount: string | null; currency: string | null };
const MODELS = ["per_booking", "per_event", "monthly"] as const;

export const FeeComparison = () => {
  const { t } = usePrefs();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [price, setPrice] = useState<Record<string, string>>({ per_booking: "", per_event: "", monthly: "" });
  const [currency, setCurrency] = useState("IQD");
  useEffect(() => { api.get("/admin/fee-comparison").then((r) => setRows(r.data.rows)).catch(() => setRows([])); }, []);
  const total = (r: Row, m: string) => {
    const p = Number(price[m]) || 0;
    return m === "per_booking" ? p * r.attended_bookings : m === "per_event" ? p * r.finished_events : p;
  };
  const any = MODELS.some((m) => Number(price[m]) > 0);
  return (
    <section data-testid="fee-comparison" className="rounded-3xl border bg-card p-5 mb-6 space-y-4">
      <div><h2 className="font-display text-lg font-black">{t("fc.title")}</h2><p className="text-sm text-muted-foreground">{t("fc.sub")}</p></div>
      <div className="grid gap-2 sm:grid-cols-4">
        {MODELS.map((m) => (
          <label key={m} className="text-xs font-bold space-y-1">{t(`fm.${m}`)}
            <Input inputMode="decimal" value={price[m]} onChange={(e: any) => setPrice({ ...price, [m]: e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1") })} data-testid={`fc-price-${m}`} className="h-10 rounded-full" dir="ltr" placeholder="0" />
          </label>
        ))}
        <label className="text-xs font-bold space-y-1">{t("fc.currency")}
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="fc-currency" className="h-10 w-full rounded-full border bg-background px-3 text-sm">
            <option value="IQD">IQD</option><option value="USD">USD</option>
          </select>
        </label>
      </div>
      {rows && !rows.length && <p className="text-sm text-muted-foreground">{t("ms.empty")}</p>}
      <div className="grid gap-2">
        {rows?.map((r) => {
          const vals = MODELS.map((m) => total(r, m));
          const best = any ? Math.max(...vals) : -1;
          return (
            <div key={r.account_id} data-testid={`fc-row-${r.account_id}`} className="rounded-2xl bg-muted/60 p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-bold">{r.business_name}</p>
                <span className="text-xs text-muted-foreground">{t("fc.activity").replace("{b}", String(r.attended_bookings)).replace("{e}", String(r.finished_events))} · {t("fc.current")}: {t(`fm.${r.model}`)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {MODELS.map((m, i) => (
                  <div key={m} data-testid={`fc-total-${m}-${r.account_id}`} className={`rounded-xl px-2 py-2 ${any && vals[i] === best && best > 0 ? "bg-emerald-500/15 ring-1 ring-emerald-500/40" : "bg-background"}`}>
                    <p className="text-[11px] leading-tight text-muted-foreground break-words min-h-[2.2em]">{t(`fm.${m}`)}</p>
                    <p className="font-bold text-sm"><bdi>{fmtMoney({ amount: String(vals[i]), currency } as any)}</bdi></p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
